// @givmo/app-admin — program-utilization decode + copy (EP-WEB9).
//
// `GET /admin/company/match-utilization` answers "how much of the match budget is claimed this year"
// with `{basis, numerator, denominator, ratio, year_budget_per_employee, active_employees, year}`.
// Turning that into a card is not a formatting exercise — the wire encodes THREE distinct situations
// and each needs a different sentence:
//
//   1. a ratio exists          → show the meter, and NAME the denominator's basis on the card
//   2. `basis: none`           → the policy has no budget at all. Action: set one in Match Program.
//   3. a basis BUT no ratio    → a budgeted policy whose denominator computed to ZERO, which today
//                                means `year_budget_per_employee x 0 enrolled employees`. The honest
//                                sentence is "no employees enrolled yet", NOT "no budget set" — the
//                                backend deliberately still reports the basis so a client can tell
//                                these apart, and the remedy is inviting employees, not editing a
//                                policy.
//
// NONE of them is 0%. A fabricated 0% tells an admin their program is unused when the truth is that
// nobody has said what "fully used" would mean.
//
// TWO BASES, TWO DIFFERENT CLAIMS — the distinction is load-bearing, not pedantry:
//   `aggregate_cap`         the company set a real annual ceiling. "% of your $X annual cap" is true.
//   `per_employee_product`  there is no ceiling; the denominator is per-employee allowance x enrolled
//                           employees — what the program WOULD cost if everyone used their full
//                           allowance. Calling that "your budget" would tell an admin they are N%
//                           through a limit they never set, so the copy says "potential cost" and says
//                           out loud that it is not a cap.
//
// Money on this wire is a DECIMAL STRING (`numerator`/`denominator`) and is only ever formatted, never
// parsed for arithmetic. `ratio` is the backend's own division — we never recompute it from the two
// strings, because that would reintroduce the float rounding the server already avoided.

import type { CompanyMatchUtilization, MatchUtilizationBasis } from '@givmo/api-client';
import { formatMoneyWithSymbol } from '../settings/wallet-decode';

/** The three renderable outcomes, as a discriminated union so a caller cannot forget one. */
export type UtilizationView =
  | {
      kind: 'ratio';
      basis: MatchUtilizationBasis;
      /** The backend's own numerator/denominator division. A fraction, not a percentage. */
      ratio: number;
      /** Formatted claimed-so-far, e.g. "$4,200.00". */
      numerator: string;
      /** Formatted denominator, e.g. "$10,000.00". */
      denominator: string;
      year: number;
    }
  | { kind: 'no-budget'; numerator: string; year: number }
  | { kind: 'no-employees'; basis: MatchUtilizationBasis; numerator: string; year: number };

/**
 * Reduce the wire to one of the three views. Tolerant by construction: an absent payload, a non-finite
 * ratio, or an unformattable denominator all fall back to a state that renders an action rather than a
 * number — never a throw, never a fabricated figure.
 */
export function toUtilizationView(
  data: CompanyMatchUtilization | undefined,
): UtilizationView | null {
  if (!data) return null;

  const year = Number.isFinite(data.year) ? data.year : new Date().getFullYear();
  // A blank/absent numerator means "nothing claimed", which IS a real $0 — unlike a missing budget, a
  // claimed-total of zero is a fact the wire is asserting, so it is safe to show as money.
  const numerator = formatMoneyWithSymbol(data.numerator) ?? '$0.00';

  if (data.basis === 'none') return { kind: 'no-budget', numerator, year };

  const ratio = data.ratio;
  const denominator =
    data.denominator === null || data.denominator === undefined
      ? null
      : formatMoneyWithSymbol(data.denominator);

  if (typeof ratio !== 'number' || !Number.isFinite(ratio) || denominator === null) {
    // A basis without a usable ratio: the denominator computed to zero (or arrived unreadable).
    return { kind: 'no-employees', basis: data.basis, numerator, year };
  }

  return { kind: 'ratio', basis: data.basis, ratio, numerator, denominator, year };
}

/** The headline percentage, rounded to a whole point — the meter's `valueText` and the big number. */
export function utilizationPercentLabel(view: Extract<UtilizationView, { kind: 'ratio' }>): string {
  return `${Math.round(view.ratio * 100)}%`;
}

/**
 * The caption printed ON the card, naming the denominator. Deliberately not tucked into the methodology
 * popover: a percentage whose denominator is invisible is the number that gets quoted in a meeting and
 * turns out to have meant something else.
 */
export function utilizationBasisCaption(view: Extract<UtilizationView, { kind: 'ratio' }>): string {
  return view.basis === 'aggregate_cap'
    ? `of your ${view.denominator} annual cap`
    : `of a ${view.denominator} potential program cost — not a committed cap`;
}

/** The methodology text for whichever state the card is in (§135). */
export function utilizationMethodology(view: UtilizationView | null): string {
  const numeratorSentence =
    'The amount counted is approved employee match requests for the calendar year — the same figure the ' +
    'budget check uses when it decides whether an approval can go through, so this percentage and that ' +
    'check never disagree. Direct company gifts are excluded: they never consumed match budget.';

  if (view === null) {
    return `Program utilization compares match spending against your match budget. ${numeratorSentence}`;
  }

  if (view.kind === 'no-budget') {
    return `No match budget is set, so there is nothing to measure against — which is why no percentage is shown rather than 0%. ${view.numerator} in matches has been approved in ${view.year}. ${numeratorSentence}`;
  }

  if (view.kind === 'no-employees') {
    return `Your budget is set per employee and no employees are enrolled yet, so the total budget is currently zero and a percentage would be meaningless. ${view.numerator} in matches has been approved in ${view.year}. ${numeratorSentence}`;
  }

  const denominatorSentence =
    view.basis === 'aggregate_cap'
      ? `Compared against the ${view.denominator} annual company cap in your match policy.`
      : `Compared against ${view.denominator} — your per-employee annual allowance multiplied by your enrolled employees. That is what the program would cost if every employee used their full allowance; it is not a cap you have committed to.`;

  return `${view.numerator} of ${view.denominator} claimed in ${view.year}. ${denominatorSentence} ${numeratorSentence}`;
}
